import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Save, X, ChevronLeft, ChevronRight, Search } from "lucide-react";

interface WorkspaceUsageRow {
  workspace: { id: string; companyName: string };
  quota: { budgetCents: number; warningThreshold: number } | null;
  usedCents: number;
}

interface AiUsageResponse {
  data: WorkspaceUsageRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 10;

function usageStatusColor(percent: number) {
  if (percent > 80) return { bg: "bg-red-100", text: "text-red-800", dot: "bg-red-500" };
  if (percent >= 60) return { bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" };
  return { bg: "bg-green-100", text: "text-green-800", dot: "bg-green-500" };
}

async function fetchAiUsage(page: number, search: string): Promise<AiUsageResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: PAGE_SIZE.toString(),
    search,
  });
  const res = await fetch(`/api/admin/ai-usage?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch");
  return res.json();
}

export default function AdminAiUsage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ai-usage", page, search],
    queryFn: () => fetchAiUsage(page, search),
  });

  const rows = data?.data ?? [];

  const saveBudget = useMutation({
    mutationFn: async ({
      workspaceId,
      budgetCents,
    }: {
      workspaceId: string;
      budgetCents: number;
    }) => {
      const res = await fetch(`/api/admin/workspaces/${workspaceId}/ai-quota`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ budgetCents }),
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: (_, { workspaceId }) => {
      qc.invalidateQueries({ queryKey: ["admin-ai-usage"] });
      setEditing((prev) => {
        const next = { ...prev };
        delete next[workspaceId];
        return next;
      });
      toast({ title: "Budget updated" });
    },
    onError: () => toast({ title: "Failed to update budget", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-black tracking-tight text-primary">AI Usage</h1>
        <p className="text-lg text-muted-foreground">Monitor and manage workspace AI quotas</p>
      </div>

      {/* Workspace Quotas Card */}
      <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-6 border-b border-border/30 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-foreground">Workspace Quotas</h3>
            <p className="text-sm text-muted-foreground">Set and track AI spending limits per workspace</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9 w-56 rounded-2xl"
              placeholder="Search workspaces..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {/* Table Header */}
        <div className="bg-muted/30 grid grid-cols-[1fr_7rem_7rem_6rem_7rem_8rem] px-6 py-3">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Workspace</span>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Budget ($)</span>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Used ($)</span>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">% Used</span>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Remaining</span>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Actions</span>
        </div>

        {/* Table Rows */}
        {rows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">
            No workspaces found.
          </div>
        ) : (
          rows.map(({ workspace, quota, usedCents }) => {
            const budgetCents = quota?.budgetCents ?? 0;
            const percent =
              budgetCents > 0 ? Math.round((usedCents / budgetCents) * 100) : 0;
            const remaining = Math.max(0, budgetCents - usedCents);
            const isEditingRow = workspace.id in editing;
            const status = usageStatusColor(percent);

            return (
              <div key={workspace.id} className="grid grid-cols-[1fr_7rem_7rem_6rem_7rem_8rem] px-6 py-4 items-center border-t border-border/10">
                {/* Workspace */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-primary">
                      {workspace.companyName.slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-foreground">{workspace.companyName}</span>
                </div>

                {/* Budget */}
                <div>
                  {isEditingRow ? (
                    <Input
                      className="w-24 h-8 text-xs rounded-2xl"
                      type="number"
                      min={0}
                      step={0.01}
                      value={editing[workspace.id]}
                      onChange={(e) =>
                        setEditing((prev) => ({ ...prev, [workspace.id]: e.target.value }))
                      }
                    />
                  ) : (
                    <span
                      className="text-sm text-foreground cursor-pointer hover:underline"
                      onClick={() =>
                        setEditing((prev) => ({
                          ...prev,
                          [workspace.id]: String(budgetCents / 100),
                        }))
                      }
                    >
                      ${(budgetCents / 100).toFixed(2)}
                    </span>
                  )}
                </div>

                {/* Used */}
                <span className="text-sm text-foreground">${(usedCents / 100).toFixed(4)}</span>

                {/* % Used */}
                <div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                    {percent}%
                  </span>
                </div>

                {/* Remaining */}
                <span className="text-sm text-foreground">${(remaining / 100).toFixed(2)}</span>

                {/* Actions */}
                <div className="flex justify-end gap-1">
                  {isEditingRow ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          const val = parseFloat(editing[workspace.id]);
                          if (!isNaN(val) && val >= 0) {
                            saveBudget.mutate({
                              workspaceId: workspace.id,
                              budgetCents: Math.round(val * 100),
                            });
                          }
                        }}
                        disabled={saveBudget.isPending}
                        title="Save"
                      >
                        <Save className="h-4 w-4 text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() =>
                          setEditing((prev) => {
                            const next = { ...prev };
                            delete next[workspace.id];
                            return next;
                          })
                        }
                        title="Cancel"
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() =>
                        setEditing((prev) => ({
                          ...prev,
                          [workspace.id]: String(budgetCents / 100),
                        }))
                      }
                      title="Edit budget"
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-border/10 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, data.total)} of {data.total} results
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <div className="text-sm font-medium">
                Page {page} of {data.totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
